// Events adapter (LIFE_CYCLE_EVENT_BUS). Single wrapper over the host page's global
// lifecycle event bus, sitting beside broadcast.js (the cross-tab adapter). It folds
// in the defensive guards the call sites used to repeat — or, in a few spots, omit —
// so an absent/non-standard bus can never throw during a trigger or during teardown.
//
// The bus is reached in four ways across the app; this adapter parametrizes both axes
// instead of scattering the access shapes:
//   - frame:   'self'   → window.LIFE_CYCLE_EVENT_BUS        (assistant's own events)
//              'parent' → window.parent.LIFE_CYCLE_EVENT_BUS (host-page / cross-frame)
//   - channel: 'default'   → bus.trigger/on/off              (top-level events)
//              'lifeCycle'  → bus.lifeCycle.trigger/on/off    (the nested lifeCycle bus)
//
// The assistant renders inside the THD host page (same-origin), so parent access is
// expected; the SSR/absent-bus guards keep it inert everywhere else.

const resolveBus = (frame) => {
  if (typeof window === 'undefined') return undefined;
  const root = frame === 'parent' ? window.parent : window;
  return root ? root.LIFE_CYCLE_EVENT_BUS : undefined;
};

const resolveChannel = (bus, channel) => {
  if (!bus) return undefined;
  return channel === 'lifeCycle' ? bus.lifeCycle : bus;
};

/**
 * Fire a lifecycle event. No-op (never throws) when the bus or channel is absent.
 * @param {string} eventName
 * @param {*} [payload]
 * @param {{ frame?: 'self'|'parent', channel?: 'default'|'lifeCycle' }} [options]
 */
export const emit = (eventName, payload, { frame = 'self', channel = 'default' } = {}) => {
  const target = resolveChannel(resolveBus(frame), channel);
  if (target?.trigger) target.trigger(eventName, payload);
};

/**
 * Subscribe a handler to a lifecycle event. Returns an unsubscribe function that targets
 * the SAME bus resolved at subscribe time — matching the existing effects that capture
 * the bus at mount so teardown can't hit a swapped or absent bus. No-op when absent.
 *
 * ⚠ `channel: 'lifeCycle'` REPLAYS: subscribe mount-only (`[]` deps), and use
 * `unsubscribeAll` when the buffer must not survive teardown.
 * See .claude/context/event-bus.md
 *
 * @param {string} eventName
 * @param {Function} handler
 * @param {{ frame?: 'self'|'parent', channel?: 'default'|'lifeCycle' }} [options]
 * @returns {() => void} unsubscribe
 */
export const subscribe = (eventName, handler, { frame = 'self', channel = 'default' } = {}) => {
  const target = resolveChannel(resolveBus(frame), channel);
  if (target?.on) target.on(eventName, handler);
  return () => {
    if (target?.off) target.off(eventName, handler);
  };
};

// --- Assistant readiness handshake ---
//
// A contract with the host page's entry points, not an internal event: they wait on it before
// handing over a question. Parent frame + lifeCycle channel; only whatever unmounts the
// `user-ask-question` listener may retract; never re-announce off drawer traffic.
// See .claude/context/event-bus.md
let assistantReadyAnnounced = false;

/** Announce that the assistant can accept a question, once it genuinely can. */
export const announceAssistantReady = () => {
  assistantReadyAnnounced = true;
  emit('magic-apron-assistant.isReady', { isReady: true }, { frame: 'parent', channel: 'lifeCycle' });
};

/**
 * Withdraw the announcement. ⚠ LEGACY tree only, from its unmount — the consolidated tree
 * stays subscribed while CLOSED, so retracting for it drops entry-point questions.
 */
export const retractAssistantReady = () => {
  assistantReadyAnnounced = false;
  emit('magic-apron-assistant.isReady', { isReady: false }, { frame: 'parent', channel: 'lifeCycle' });
};

/**
 * Has readiness been announced (and not withdrawn)? Seed late subscribers from this rather
 * than re-emitting. Tracks OUR announcements only — the legacy package emits directly too.
 * @returns {boolean}
 */
export const hasAnnouncedAssistantReady = () => assistantReadyAnnounced;

/**
 * Subscribe to the readiness announcement, on the same bus `announceAssistantReady` emits to.
 * @param {(isReady: boolean) => void} handler
 * @returns {() => void} unsubscribe
 */
export const subscribeAssistantReady = (handler) => subscribe(
  'magic-apron-assistant.isReady',
  ({ output } = {}) => handler(output?.isReady !== false),
  { frame: 'parent', channel: 'lifeCycle' },
);

/**
 * Remove ALL handlers for an event (defensive clear — the bus's `off(name)` with no
 * handler). No-op when the bus/channel is absent.
 * @param {string} eventName
 * @param {{ frame?: 'self'|'parent', channel?: 'default'|'lifeCycle' }} [options]
 */
export const unsubscribeAll = (eventName, { frame = 'self', channel = 'default' } = {}) => {
  const target = resolveChannel(resolveBus(frame), channel);
  if (target?.off) target.off(eventName);
};
